// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A lista de pedidos — tipos e lógica pura
// ============================================================================
//
// 📗 fonte 5.3.3.1. O que aqui está é a **leitura** da lista: como se ordena, que
// marcadores tem, que estados são legítimos. O desenho está em `Pedidos.tsx`.
//
// Separado por ser a parte que se testa sem DOM — e a ordenação da fila é
// precisamente o que erra em silêncio: uma ordem errada não parece mal, só faz o
// planeador trabalhar pela ordem errada.
// ============================================================================

import { aEsperaDe, antesDePlanear, foraDoPlaneamento, type DestinoFinal } from '@xlv-gva-mpt/dominio';

/** Estados da máquina especificada em `modelo-de-dados-proposta` §1.2. */
export type EstadoPedido =
  // ⚠️ 098 · pediram preço — não entra no planeamento até ser convertido (`foraDoPlaneamento`).
  | 'orcamento'
  | 'recebido'
  | 'em_validacao'
  | 'aguarda_informacao'
  | 'aguarda_aprovacao'
  | 'novo'
  | 'reservado'
  | 'confirmado'
  | 'executado'
  | 'cancelado';

export interface PontoPedido {
  readonly tipo: 'recolha' | 'largada';
  readonly data: string;
  readonly hora: string;
  readonly localidade: string;
  readonly voo: string;
  readonly horaPresenca: string;
  readonly margemH: string;
}

export interface Pedido {
  readonly id: string;
  readonly plano: string;
  /** ⚠️ O ID, e não só a sigla: é o que a reserva grava. Ver `PedidoListado`. */
  readonly clienteId: string;
  readonly clienteSigla: string;
  readonly clienteNome: string;
  readonly itinerario: string;
  readonly referencia: string;
  readonly dataInicio: string;
  readonly dataFim: string;
  readonly dia0: number;
  readonly dia1: number;
  readonly lotacao: number;
  readonly requisitos: string;
  readonly estado: EstadoPedido;
  readonly urgente: boolean;
  readonly activo: boolean;
  readonly prioridade: number;
  readonly duvida: boolean;
  readonly origem: string;
  readonly natureza: string;
  readonly recolha: PontoPedido | null;
  /** ⚠️ Só o REGISTADO — é o que o formulário de edição e a reserva escrevem. Ver `destinoFinal`. */
  readonly largada: PontoPedido | null;
  /**
   * ⚠️⚠️ **Onde o pedido TERMINA — a largada registada, ou a PRESUMIDA (o local do dia anterior).**
   *
   * 📗 Cliente, 2026-09-29: *«quando não se sabe, o melhor é assumir o do dia anterior e colocar
   *    um ponto de interrogação»*. Calculado pelo servidor com `destinoFinalDoPedido` e nunca
   *    gravado. Quem o mostra usa `rotuloDoDestinoFinal` (o `?`) e `TITULO_DESTINO_PRESUMIDO`.
   *
   * ⚠️ **Opcional de propósito:** ausente = um servidor anterior a este campo. Nesse caso
   * `destinoFinalDe` (`destino-final-no-ecra.ts`) recalcula-o pela MESMA função a partir da
   * recolha e da largada — nunca o lê como «registado». Não traz hora: a hora é `largada.hora`.
   */
  readonly destinoFinal?: DestinoFinal | null;
  /** 📗 fonte 6.8, D-079: **texto livre, e o sistema não o interpreta.** */
  readonly notas?: string;
  /** O contrato de onde veio, quando veio de um (📗 fonte §2). Vazio é o caso normal. */
  readonly contrato?: string;
  /**
   * ⚠️ O que a COLUNA diz, quando discorda do que o PLANO prova.
   *
   * Ausente quando concordam — que é quase sempre. Mostrar os dois estados em todas as
   * linhas ensinaria a ignorá-los; mostrá-lo só na divergência é o que o torna um sinal.
   */
  readonly estadoRegistado?: string;
  /**
   * Quantos dias deste pedido já têm alocação. ⚠️ Vem do `/api/pedidos` desde sempre e o web
   * ignorava-o; é o que permite ver, **sem uma chamada por linha**, que uma viagem está colocada
   * só em parte. `undefined` sem base de dados — ver `viagemPorCompletar`.
   */
  readonly alocacoes?: number;
  /**
   * Quantas dessas alocações ainda são RESERVA (por confirmar). ⚠️ Vem do `/api/pedidos` com
   * `alocacoes`; a diferença é quantos dias estão confirmados — é o que decide a caixa «libertar
   * os dias» ao cancelar (`libertarDiasPorOmissao`). `undefined` sem base de dados.
   */
  readonly reservas?: number;
  /**
   * As viaturas onde este pedido está reservado ou confirmado.
   *
   * ⚠️ **No plural**: 43 % das referências reais aparecem em duas viaturas (F-22) — a mesma
   * viagem partida em duas é o caso normal, e levar o planeador só a uma mostrava-lhe metade
   * do serviço.
   */
  readonly viaturaIds?: readonly string[];
  /**
   * ⚠️⚠️ **OS NUMEROS INTERNOS das mesmas viaturas — o nome pelo qual as pessoas as tratam.**
   *
   * 📗 Cliente, 2026-08-12: *«no ver mapa vai para outra»*. 📌 Medido: a procura por «pne» em
   * 01–02 de Janeiro devolve **11 pedidos**, todos do mesmo cliente e quase com as mesmas datas
   * — indistinguiveis na lista. Sem o numero da viatura nao ha como saber qual e o que se tem
   * debaixo do olho no mapa, e o «Ver no mapa» parece ir para o sitio errado quando vai para o
   * certo. 📌 `viaturaIds` ja vinha e nao serve para ler: `V0012` nao e como se chama a 111.
   */
  readonly viaturaNumeros?: readonly string[];
  /** Dias de viatura própria sem ninguém atribuído. É o que impede a reserva de sair. */
  readonly semCondutor?: number;
}

/**
 * Rótulo e papel de cor de cada estado.
 *
 * ⚠️ **Os papéis são os da paleta validada** (`bom`, `aviso`, `grave`) e não cores
 * escolhidas aqui. A regra do sistema de cor é: **quente é problema**, verde é
 * conforme, frio e púrpura são clientes. Inventar uma cor nova para um estado punha
 * um tom quente a competir com o alarme — e a paleta foi validada contra isso.
 */
export const ESTADOS: Readonly<
  Record<EstadoPedido, { readonly rotulo: string; readonly papel: 'bom' | 'aviso' | 'grave' | 'neutro' }>
> = {
  // ⚠️ 098 · neutro e não quente: um orçamento não é um problema, é trabalho que ainda não é nosso.
  orcamento: { rotulo: 'orçamento', papel: 'neutro' },
  recebido: { rotulo: 'recebido', papel: 'neutro' },
  em_validacao: { rotulo: 'em validação', papel: 'neutro' },
  aguarda_informacao: { rotulo: 'aguarda informação', papel: 'aviso' },
  aguarda_aprovacao: { rotulo: 'aguarda aprovação', papel: 'aviso' },
  novo: { rotulo: 'novo', papel: 'bom' },
  reservado: { rotulo: 'reservado', papel: 'aviso' },
  confirmado: { rotulo: 'confirmado', papel: 'bom' },
  executado: { rotulo: 'executado', papel: 'neutro' },
  cancelado: { rotulo: 'cancelado', papel: 'grave' },
};

/**
 * A ordem da fila de trabalho.
 *
 * 📗 *«O planeador pode dar prioridades aos pedidos de 0 a 10 para ordenar prioridades
 * de planeamento e subir ou descer na lista a ordem pela qual vai planear os pedidos.»*
 *
 * ⚠️ **Urgente não é prioridade 10.** A fonte lista `urgente` junto dos estados, mas
 * trata-o como marcador — e um pedido pode ser urgente com prioridade média: é o
 * telefonema que entra a meio da manhã. Se fosse prioridade, não se podia distinguir
 * «importante» de «agora».
 *
 * Entre dois igualmente prioritários decide a **data de início**: é o mais próximo que
 * arde.
 */
export function ordenarFila(pedidos: readonly Pedido[]): readonly Pedido[] {
  return [...pedidos].sort(
    (a, b) =>
      Number(b.urgente) - Number(a.urgente) ||
      b.prioridade - a.prioridade ||
      a.dataInicio.localeCompare(b.dataInicio),
  );
}

/**
 * O pedido está pronto a planear?
 *
 * 📗 Só o estado `novo` é «validado e pronto a planear» (5.3.1: aparece no dashboard
 * como «Novo» **após validação humana**). Os outros estão à espera de alguém.
 *
 * ⚠️ Um pedido `inactivo` também não: 📗 `inactivo` é «fora da lista de trabalho, sem
 * estar cancelado». Planear um pedido que alguém pôs de lado desfazia essa intenção.
 */
export function prontoAPlanear(p: Pedido): boolean {
  return p.estado === 'novo' && p.activo;
}

/**
 * As DUAS listas: o que está por planear e o que já está reservado ou confirmado.
 *
 * 📗 Pedido do cliente (2026-08-06): *«quando eu reservo tem de desaparecer esse pedido dos
 * pedidos a planear e pode passar para uma nova lista de reservas»*.
 *
 * ⚠️ **Porque é que isto não era só um filtro:** o estado do pedido vinha do CSV e uma
 * reserva gravada na base **nunca o mudava**. O pedido ficava `novo` para sempre e o
 * planeador podia reservá-lo outra vez, noutra viatura — duas viaturas para o mesmo grupo,
 * sem nada no ecrã a denunciá-lo. A causa estava no servidor (a derivação a partir das
 * alocações) e a separação das listas é o que a torna visível.
 *
 * ⚠️ E os reservados ficam **agrupados por mês**, não numa lista corrida: uma reserva
 * consulta-se por «o que tenho marcado em Agosto», não por prioridade — a prioridade
 * ordena o que ainda está por decidir, e isto já está decidido.
 */
export function separarListas(pedidos: readonly Pedido[]): {
  readonly aPlanear: readonly Pedido[];
  readonly reservados: readonly Pedido[];
  readonly bloqueados: readonly Pedido[];
  /**
   * ⚠️⚠️ **O QUARTO SEPARADOR — o que ja aconteceu ou foi cancelado.**
   *
   * 📗 Cliente, 2026-08-12: *«a viagem 111 nao aparece nos reservados... porque?? nao aparece na
   * lista de reservadas»*.
   *
   * 📌 **Medido na rota com os filtros exactos dele** (`texto=pne&de=2026-01-01&ate=2026-01-02`):
   * `encontrados=11` — **10 `executado` + 1 `reservado`** —, e o pedido da viatura 111 (`P001257`)
   * estava la, com estado `executado`. O ecra punha-o em **«A planear»**, que e falso sobre ele:
   * uma viagem de Janeiro ja feita nao esta por planear, e por isso ele nao a encontrou.
   *
   * ⚠️⚠️ **Era o `else` FINAL a apanhar tudo, e o comentario abaixo ja o admitia** («e ate os
   * cancelados e os ja executados»). A correccao dos bloqueados tratou os estados ANTES de `novo`
   * e deixou os DEPOIS de `confirmado` no saco. E a mesma familia do separador «Sem regressar»,
   * que aparecia em todos os separadores por ser o `else` de outro.
   *
   * 📌 E ha agora um teste que soma os quatro separadores e exige o total: um estado novo cai em
   * algum sitio, ou a conta falha. Sem ele, isto volta a acontecer ao decimo estado.
   */
  readonly historico: readonly Pedido[];
} {
  const aPlanear: Pedido[] = [];
  const reservados: Pedido[] = [];
  const bloqueados: Pedido[] = [];
  const historico: Pedido[] = [];
  for (const p of pedidos) {
    if (p.estado === 'reservado' || p.estado === 'confirmado') reservados.push(p);
    // ⚠️ O que JA ACONTECEU ou foi CANCELADO. Ver a nota do campo `historico`.
    else if (p.estado === 'executado' || p.estado === 'cancelado') historico.push(p);
    // ⚠️⚠️ **UM PEDIDO BLOQUEADO NÃO É «POR PLANEAR», E A CONTAGEM MENTIA.**
    //
    //    📗 Cliente, 2026-08-08: *«tem ainda alguns pedidos à espera de aprovação e à espera
    //    de cliente… isso ainda não está planeado, qual é a ideia?»*.
    //
    //    Tudo o que não estivesse reservado caía em «A planear» — incluindo os que **não
    //    podem ser planeados** (`razaoParaNaoPlanear` recusa-os) e até os cancelados e os já
    //    executados. 📌 «A planear 14» é o número mais lido deste ecrã e é ele que diz quanto
    //    trabalho há; enchê-lo de coisas que ninguém pode fazer é a forma mais eficaz de o
    //    tornar inútil — e de os bloqueados nunca serem tratados, porque ficam misturados
    //    com os que se podem fazer já.
    //
    // ⚠️⚠️ **SÓ OS ESTADOS ANTES DE `novo`, e a primeira versão desta linha estava errada.**
    //
    //    Usei `razaoParaNaoPlanear(p) !== null`, o que parecia elegante — uma regra só. Mas
    //    essa função recusa quatro coisas diferentes, e duas delas **têm de ficar na fila**:
    //    um pedido `inactivo` fica **esbatido e etiquetado, nunca escondido** (a regra dos
    //    filtros do mapa, com teste a segurá-la), e um `novo` sem local de recolha fica ao
    //    lado dos outros — o sítio onde alguém preenche o que falta é a fila, não uma gaveta.
    //    📌 Foram os testes que já existiam a apanhar-me.
    else if (antesDePlanear(p.estado)) bloqueados.push(p);
    else aPlanear.push(p);
  }
  return {
    // ⚠️ Por «à espera de quem» e depois por data: quem varre esta lista está a decidir a
    //    quem telefonar, e a prioridade — que ordena o que é NOSSO decidir — não ajuda aqui.
    bloqueados: [...bloqueados].sort(
      (a, b) =>
        String(aEsperaDe(a.estado)).localeCompare(String(aEsperaDe(b.estado))) ||
        a.dataInicio.localeCompare(b.dataInicio),
    ),
    aPlanear: ordenarFila(aPlanear),
    // Por data, e não por prioridade: já está decidido.
    reservados: [...reservados].sort((a, b) => a.dataInicio.localeCompare(b.dataInicio)),
    // ⚠️ O histórico vem do MAIS RECENTE para trás: quem o abre está a procurar o que acabou de
    //    acontecer, não o que aconteceu em Janeiro. É a ordem inversa da dos reservados, e de
    //    propósito — uma lista de futuro lê-se para a frente, uma de passado para trás.
    historico: [...historico].sort((a, b) => b.dataInicio.localeCompare(a.dataInicio)),
  };
}

/** Agrupa por `aaaa-mm`, preservando a ordem de entrada dentro de cada mês. */
export function porMes(pedidos: readonly Pedido[]): readonly (readonly [string, Pedido[]])[] {
  const mapa = new Map<string, Pedido[]>();
  for (const p of pedidos) {
    const chave = p.dataInicio.slice(0, 7);
    const lista = mapa.get(chave);
    if (lista === undefined) mapa.set(chave, [p]);
    else lista.push(p);
  }
  return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

/** Porque é que este pedido não pode ser planeado agora. `null` se puder. */
export function razaoParaNaoPlanear(p: Pedido): string | null {
  if (!p.activo) return 'está inactivo — foi posto de lado sem ser cancelado';
  // ⚠️⚠️ **UM PEDIDO JÁ RESERVADO NÃO ESTÁ «À ESPERA DE VALIDAÇÃO», E A MENSAGEM DIZIA ISSO.**
  //
  //    📗 Cliente, 2026-08-08: *«quando um pedido está reservado e não dá para avançar por
  //    falta de preenchimento, como poderei preencher? como poderei avançar?»*.
  //
  //    A frase única — «só se planeia depois de validado» — é verdadeira para quem ainda
  //    espera informação e **falsa** para quem já tem autocarro: manda esperar por uma
  //    validação que já aconteceu. 📌 Uma instrução errada num botão apagado é pior do que
  //    nenhuma: quem a lê fica à espera de uma coisa que nunca vai chegar.
  if (p.estado === 'reservado' || p.estado === 'confirmado') {
    return 'já tem viatura — planear outra vez daria um segundo autocarro ao mesmo grupo';
  }
  // ⚠️⚠️ **E O QUE JÁ ACONTECEU NÃO ESPERA POR VALIDAÇÃO NENHUMA — mesma família da frase acima.**
  //
  //    📌 Encontrado a 2026-08-12 ao dar lista ao separador «Histórico»: um pedido `executado` ou
  //    `cancelado` caía no `!== 'novo'` e o botão dizia *«só se planeia depois de validado»* — que
  //    manda esperar por uma validação que nunca vai chegar, sobre uma viagem que já foi feita ou
  //    desfeita. É exactamente o defeito que o comentário de cima descreve para os reservados, no
  //    estado seguinte. **Uma instrução errada num botão apagado é pior do que nenhuma.**
  if (p.estado === 'executado') {
    return 'já foi executado — o que passou não se planeia, consulta-se';
  }
  if (p.estado === 'cancelado') {
    return 'foi cancelado — para o refazer, crie um pedido novo';
  }
  // ⚠️⚠️ 098 · **O ORÇAMENTO, com a frase do domínio** — a mesma que a reserva devolve no 409.
  //    Sem esta linha caía no ramo de baixo e dizia «só se planeia depois de validado», que manda
  //    validar o que é preciso CONVERTER.
  const foraDoPlano = foraDoPlaneamento(p.estado);
  if (foraDoPlano !== null) return foraDoPlano;
  if (p.estado !== 'novo') {
    return `está em «${ESTADOS[p.estado].rotulo}» — só se planeia depois de validado`;
  }
  if (p.recolha === null || p.recolha.localidade === '') {
    // Sem local de recolha o motor não tem destino para onde mobilizar. É a lacuna
    // que a fonte 5.3.2.2 prevê: «faltam informações relevantes».
    return 'não diz onde recolher o grupo — sem isso o motor não tem destino';
  }
  return null;
}

/** As tags que a fonte pede: «cores, tags e marcadores». */
export function tags(p: Pedido): readonly string[] {
  const t: string[] = [];
  if (p.urgente) t.push('urgente');
  if (!p.activo) t.push('inactivo');
  if (p.duvida) t.push('provisório');
  if (p.natureza === 'ao_abrigo_contrato') t.push('contrato');
  if (p.natureza === 'confirmacao_final') t.push('confirmação');
  if (p.natureza === 'cancelamento') t.push('cancelamento');
  if (p.origem === 'telefone') t.push('telefone');
  if (p.recolha?.voo !== undefined && p.recolha.voo !== '') t.push('voo');
  return t;
}

/** Sobe ou desce na lista, dentro de 0 a 10. */
export function comPrioridade(p: Pedido, delta: number): Pedido {
  return { ...p, prioridade: Math.max(0, Math.min(10, p.prioridade + delta)) };
}

/**
 * ⚠️⚠️ **OS BLOCOS DE DIAS DO PEDIDO QUE AINDA NÃO TÊM AUTOCARRO.**
 *
 * 📗 Cliente, 2026-08-19: *«o planear já está adaptado para isso e lida com isso?»* — sobre uma
 * viagem partida entre dois autocarros.
 *
 * ⚠️⚠️ **A resposta medida era NÃO, e o beco foi criado por mim a 2026-08-18.** O «Planear» propõe
 * *«partir entre dois autocarros»* e **não escreve** (exige duas escritas); o planeador reserva a
 * primeira metade, o pedido passa a `reservado`, e `prontoAPlanear` — `estado === 'novo'` — faz o
 * botão **desaparecer**. A segunda metade ficava sem gesto.
 *
 * 📌 Medido no `dev`: **5** viagens por completar, **1** já sem botão. Raro nos dados de hoje
 * porque o operador é novo — mas é o caminho que ele abre.
 *
 * ⚠️ **Devolve BLOCOS e não uma contagem:** os dias que faltam podem não ser contíguos (a viagem
 * pode estar colocada nas pontas e vazia no meio), e uma reserva cobre **um intervalo**. Dizer
 * «faltam 4 dias» sobre dois blocos separados fazia o gesto seguinte reservar dias que já têm
 * autocarro.
 *
 * @param dia0 O primeiro dia do pedido, no ano.
 * @param dia1 O último dia do pedido.
 * @param cobertos Os dias que já têm alocação — por índice no ano, em qualquer ordem.
 */
export function blocosPorCobrir(
  dia0: number,
  dia1: number,
  cobertos: readonly number[],
): readonly { readonly de: number; readonly ate: number }[] {
  const tem = new Set(cobertos);
  const saida: { de: number; ate: number }[] = [];
  let aberto: { de: number; ate: number } | null = null;
  for (let d = dia0; d <= dia1; d++) {
    if (tem.has(d)) {
      if (aberto !== null) {
        saida.push(aberto);
        aberto = null;
      }
      continue;
    }
    if (aberto === null) aberto = { de: d, ate: d };
    else aberto.ate = d;
  }
  if (aberto !== null) saida.push(aberto);
  return saida;
}

/**
 * A viagem tem autocarro em alguns dias e não em todos?
 *
 * ⚠️ **Barato de propósito:** decide-se com o que a lista **já tem** (`alocacoes`, uma contagem
 * que o `/api/pedidos` devolve desde sempre), sem uma chamada por linha. Os **dias** só se vão
 * buscar quando alguém clica — e é aí que `blocosPorCobrir` entra.
 *
 * ⚠️ `alocacoes` a `undefined` é «não sei» e devolve `false`: sem base de dados a lista cai para
 * os CSV e a contagem não existe. Um «por completar» inventado punha um botão sobre nada.
 */
export function viagemPorCompletar(p: Pedido): boolean {
  const dias = p.dia1 - p.dia0 + 1;
  return p.alocacoes !== undefined && p.alocacoes > 0 && p.alocacoes < dias;
}

/**
 * 📗 2026-10-29 · **A lotação do pedido, como se escreve em TODOS os ecrãs** (regra 8: uma só
 * formulação). Um pedido sem lotação chega como `0` (o servidor faz `coalesce(p.lotacao, 0)`, ver
 * `pedidos.ts` da API) ou `null` (`/api/plano/detalhe`) — e «0 lugares» é uma CONTA, um valor
 * plausível no lugar de «ninguém disse». Nenhum autocarro leva zero pessoas, logo `0` aqui quer
 * dizer «não indicado», e diz-se: «lotação por indicar».
 *
 * `'longa'` — «1 lugar» / «54 lugares» (o Planear, o detalhe); `'curta'` — «54 lug.» (as listas).
 * O guarda estrutural está em `lotacao-por-indicar.test.tsx`.
 */
export const LOTACAO_POR_INDICAR = 'lotação por indicar';
export function textoDaLotacao(lotacao: number | null | undefined, forma: 'longa' | 'curta' = 'longa'): string {
  if (lotacao === null || lotacao === undefined || !Number.isFinite(lotacao) || lotacao <= 0) return LOTACAO_POR_INDICAR;
  if (forma === 'curta') return `${String(lotacao)} lug.`;
  return `${String(lotacao)} ${lotacao === 1 ? 'lugar' : 'lugares'}`;
}
